import { useState, useEffect } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { useOrgTimezone, formatDateTimeInTz } from '@/utils/timezone';
import PageHeader from '../../components/PageHeader';
import Button from '../../components/ui/Button';
import {
  JobStatusTimeline,
  JobCoverageMap,
  JobLivePanel,
  JobCompletionSummary,
  JobStatsPanel,
} from '../../components/jobs';
import { Apple, ClipboardList, Edit, XCircle, Users, Wrench, MapPin, Calendar, Play } from 'lucide-react';
import jobService from '../../services/jobs';
import JobProductsCard from '../../components/jobs/JobProductsCard';
import { useModuleLicense } from '../../hooks/useModuleLicense';
import type { Job, LocationRecord, CoverageResult, JobLiveStatus, JobStats } from '../../types/jobs';

function JobDetail() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  // Set by JobCreate when people were left off pending an induction sign-off.
  const inductionRequested = (useLocation().state as { inductionRequested?: number } | null)?.inductionRequested ?? 0;
  const { user } = useAuth();
  const orgTz = useOrgTimezone();
  const { hasAccess: canRunCrew } = useModuleLicense('farm_jobs', ['supervisor', 'creator', 'admin']);
  const [job, setJob] = useState<Job | null>(null);
  const [loading, setLoading] = useState(true);
  const [waypoints, setWaypoints] = useState<LocationRecord[]>([]);
  const [coverage, setCoverage] = useState<CoverageResult | null>(null);
  const [cancelling, setCancelling] = useState(false);
  const [jobStats, setJobStats] = useState<JobStats | null>(null);

  const jobId = parseInt(id || '0', 10);

  const loadMapData = async (completed = false) => {
    try {
      const requests: Promise<any>[] = [
        jobService.getWaypoints(jobId),
        jobService.getCoverage(jobId),
      ];
      if (completed) requests.push(jobService.getJobStats(jobId));
      const [wpRes, covRes, statsRes] = await Promise.all(requests);
      if (wpRes.success) setWaypoints(wpRes.data);
      if (covRes.success) setCoverage(covRes.data);
      if (statsRes?.success) setJobStats(statsRes.data);
    } catch (e) {
      console.error('Load map data error', e);
    }
  };

  useEffect(() => {
    if (!jobId) return;
    (async () => {
      setLoading(true);
      try {
        const res = await jobService.getJob(jobId);
        if (res.success) {
          setJob(res.data);
          loadMapData(res.data.status === 'completed');
        }
      } catch (e) {
        console.error('Load job error', e);
      } finally {
        setLoading(false);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [jobId]);

  const handleCancel = async () => {
    if (!confirm('Are you sure you want to cancel this job?')) return;
    setCancelling(true);
    try {
      const res = await jobService.cancelJob(jobId);
      if (res.success) setJob(res.data);
    } finally {
      setCancelling(false);
    }
  };

  const handleLiveUpdate = (status: JobLiveStatus) => {
    if (status.coverage_pct != null && job) {
      setJob({ ...job, coverage_pct: status.coverage_pct });
    }
    // Refresh waypoints for map
    loadMapData();
  };

  const myAssignment = job?.assignments.find(a => a.user_id === user?.id);
  const isAssigned = !!myAssignment;
  const isActive = job?.status === 'in_progress' || job?.status === 'paused';
  const canStart = myAssignment?.status === 'assigned';
  const isCompleted = job?.status === 'completed';
  const canEdit = job && ['created', 'assigned'].includes(job.status);

  if (loading) {
    return (
      <div className="max-w-4xl mx-auto px-4 py-12 text-center">
        <div className="inline-block h-8 w-8 animate-spin rounded-full border-4 border-indigo-200 border-t-indigo-600" />
      </div>
    );
  }

  if (!job) {
    return (
      <div className="max-w-4xl mx-auto px-4 py-12 text-center">
        <p className="text-neutral-500">Job not found</p>
        <Button variant="outline" size="sm" className="mt-4" onClick={() => navigate('/jobs')}>Back to Jobs</Button>
      </div>
    );
  }

  return (
    <>
      <PageHeader
        title={job.name}
        subtitle={[job.activity_type_name, job.description].filter(Boolean).join(' · ') || undefined}
        icon={<ClipboardList className="h-6 w-6 text-white" />}
        actionButtons={
          <div className="flex gap-2">
            {canRunCrew && job.activity_category === 'harvest' && job.status !== 'completed' && job.status !== 'cancelled' && (
              <Button variant="outline" size="sm" onClick={() => navigate(`/harvest/pick?job=${job.id}`)}>
                <Apple className="h-4 w-4" /> Bins
              </Button>
            )}
            {canRunCrew && job.status !== 'completed' && job.status !== 'cancelled' && (
              <Button variant="outline" size="sm" onClick={() => navigate(`/jobs/${job.id}/crew`)}>
                <Users className="h-4 w-4" /> Crew mode
              </Button>
            )}
            {canEdit && (
              <Button variant="outline" size="sm" onClick={() => navigate(`/jobs/${job.id}/edit`)}>
                <Edit className="h-4 w-4" /> Edit
              </Button>
            )}
            {canStart && (
              <Button variant="primary" size="sm" onClick={() => navigate(`/jobs/${job.id}/operate`)}>
                <Play className="h-4 w-4" /> Start Job
              </Button>
            )}
            {isAssigned && isActive && (
              <Button variant="primary" size="sm" onClick={() => navigate(`/jobs/${job.id}/operate`)}>
                Open Operator View
              </Button>
            )}
            {!isCompleted && job.status !== 'cancelled' && (
              <Button variant="outline" size="sm" onClick={handleCancel} disabled={cancelling}
                className="text-red-600 border-red-200 hover:bg-red-50">
                <XCircle className="h-4 w-4" /> Cancel
              </Button>
            )}
          </div>
        }
      />
      <div className="max-w-4xl mx-auto px-4 py-6">
        {inductionRequested > 0 && (
          <div className="mb-4 rounded-lg border border-warning-500/40 bg-warning-50 px-4 py-3 text-sm text-warning-700 break-words">
            {inductionRequested === 1 ? 'One person was' : `${inductionRequested} people were`} left off this job because
            they are not inducted on its equipment. A supervisor has been asked to sign them off; they are added to the
            job as soon as that happens.
          </div>
        )}
        {/* Status Timeline */}
        <div className="bg-white border border-neutral-200 rounded-lg p-4 mb-6">
          <JobStatusTimeline status={job.status} />
        </div>

        {/* Completion Summary */}
        {isCompleted && <div className="mb-6"><JobCompletionSummary job={job} /></div>}

        {/* Fertiliser this job puts on, and what it used (renders nothing on other jobs) */}
        <div className="mb-6 empty:hidden"><JobProductsCard jobId={job.id} fertilising={job.activity_type_key === 'fertilising'} /></div>

        {/* GPS Stats (completed jobs with waypoints) */}
        {isCompleted && jobStats && jobStats.total_waypoints > 0 && (
          <div className="mb-6">
            <JobStatsPanel stats={jobStats} />
          </div>
        )}

        {/* Info cards grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
          {/* Target Areas */}
          {(job.targets?.length > 0 || job.target_name) && (
            <div className="bg-white border border-neutral-200 rounded-lg p-4">
              <div className="flex items-center gap-2 text-sm font-medium text-neutral-700 mb-2">
                <MapPin className="h-4 w-4" /> Target {job.targets?.length > 1 ? 'Areas' : 'Area'}
              </div>
              {job.targets && job.targets.length > 0 ? (
                <div className="space-y-1.5">
                  {job.targets.map(t => (
                    <div key={t.id} className="flex items-center justify-between">
                      <div>
                        <span className="text-neutral-800 font-medium">{t.target_name}</span>
                        <span className="text-xs text-neutral-400 ml-2 capitalize">{t.target_type.replace('_', ' ')}</span>
                      </div>
                      {t.area_hectares != null && (
                        <span className="text-sm text-neutral-500">{Number(t.area_hectares).toFixed(1)} ha</span>
                      )}
                    </div>
                  ))}
                  {job.targets.length > 1 && job.target_area_hectares && (
                    <div className="pt-1 border-t border-neutral-100 text-sm text-neutral-500 font-medium">
                      Total: {Number(job.target_area_hectares).toFixed(1)} ha
                    </div>
                  )}
                </div>
              ) : (
                <>
                  <p className="text-neutral-800 font-medium">{job.target_name}</p>
                  {job.target_area_hectares && <p className="text-sm text-neutral-500">{Number(job.target_area_hectares).toFixed(1)} ha</p>}
                </>
              )}
              {job.working_width_m && <p className="text-sm text-neutral-500 mt-1">Working width: {job.working_width_m}m</p>}
            </div>
          )}

          {/* Schedule */}
          <div className="bg-white border border-neutral-200 rounded-lg p-4">
            <div className="flex items-center gap-2 text-sm font-medium text-neutral-700 mb-2">
              <Calendar className="h-4 w-4" /> Schedule
            </div>
            {job.scheduled_start && <p className="text-sm text-neutral-600">Start: {formatDateTimeInTz(job.scheduled_start, orgTz)}</p>}
            {job.scheduled_end && <p className="text-sm text-neutral-600">End: {formatDateTimeInTz(job.scheduled_end, orgTz)}</p>}
            {job.actual_start && <p className="text-sm text-success-600">Started: {formatDateTimeInTz(job.actual_start, orgTz)}</p>}
            {job.actual_end && <p className="text-sm text-success-600">Completed: {formatDateTimeInTz(job.actual_end, orgTz)}</p>}
          </div>

          {/* Assignments */}
          <div className="bg-white border border-neutral-200 rounded-lg p-4">
            <div className="flex items-center gap-2 text-sm font-medium text-neutral-700 mb-2">
              <Users className="h-4 w-4" /> Assigned To
            </div>
            {job.assignments.length === 0 ? (
              <p className="text-sm text-neutral-400">No one assigned</p>
            ) : (
              <div className="space-y-1">
                {job.assignments.map(a => (
                  <div key={a.user_id} className="flex items-center justify-between text-sm">
                    <span className="text-neutral-800">{a.first_name} {a.last_name}</span>
                    <span className={`text-xs px-2 py-0.5 rounded-full ${
                    a.status === 'completed' ? 'bg-success-100 text-success-700' :
                    a.status === 'in_progress' ? 'bg-amber-100 text-amber-700' :
                    a.status === 'paused' ? 'bg-orange-100 text-orange-700' :
                    'bg-blue-100 text-blue-700'
                  }`}>{a.status.replace('_', ' ')}</span>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Equipment */}
          {job.equipment.length > 0 && (
            <div className="bg-white border border-neutral-200 rounded-lg p-4">
              <div className="flex items-center gap-2 text-sm font-medium text-neutral-700 mb-2">
                <Wrench className="h-4 w-4" /> Equipment
              </div>
              <div className="space-y-1">
                {job.equipment.map(eq => (
                  <div key={eq.equipment_id} className="flex items-center justify-between text-sm">
                    <span className="text-neutral-800">{eq.name}</span>
                    <span className="text-xs text-neutral-400">{eq.role === 'primary' ? 'Tractor' : 'Implement'}</span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* Live Panel (only when active) */}
        {isActive && (
          <div className="mb-6">
            <JobLivePanel jobId={jobId} isActive={isActive} onUpdate={handleLiveUpdate} />
          </div>
        )}

        {/* Coverage Map */}
        {(job.boundary_geometry || job.targets?.length || waypoints.length > 0) && (
          <div className="mb-6">
            <h3 className="text-sm font-medium text-neutral-700 mb-2">Coverage Map</h3>
            <JobCoverageMap
              boundaryGeometry={job.boundary_geometry}
              targets={job.targets}
              waypoints={waypoints}
              coverage={coverage}
            />
          </div>
        )}

        {/* Notes */}
        {job.notes && (
          <div className="bg-white border border-neutral-200 rounded-lg p-4">
            <h3 className="text-sm font-medium text-neutral-700 mb-1">Notes</h3>
            <p className="text-sm text-neutral-600">{job.notes}</p>
          </div>
        )}
      </div>
    </>
  );
}

export default JobDetail;
